<script setup lang="ts">
import { reactive, ref, watch } from "vue";
import useVuelidate from "@vuelidate/core";
import { captureException } from "@sentry/vue";

import { Address, DoctorDetails } from "../stores/application";
import { Input, Question } from "./fields";
import { addressValidator, samePostcodeValidator } from "../utils/validators";
import { useConfigStore } from "../stores/config";
import { fetchAddress, fetchAddressSuggestions } from "../services/api";
import AutoComplete from "./fields/AutoComplete.vue";
import { useToast } from "../services/toast";
import { Suggestion } from "../entities/suggestion";

const { toast } = useToast();

const props = defineProps<{
    address: Address | DoctorDetails,
    postcodeVerify?: string,
    findAddressLabel: string,
    idPrepend?: string,
    questionRow: boolean,
}>();

const v$ = useVuelidate();

const addressState = reactive({
    line1: props.address.line1,
    line2: props.address.line2,
    city: props.address.city,
    county: props.address.county,
    postcode: props.address.postcode,
    country: props.address.country,
});

const idWithPrepend = (fieldName: string) : string => {
    if (!props.idPrepend) {
        return fieldName;
    }
    return `${props.idPrepend ?? ""}_${fieldName}`;
};

const suggestions = ref([] as Suggestion[]);

const configStore = useConfigStore();

watch(() => ({ ...addressState }), (value) => {
    emit("addressUpdate", value);
});

const emit = defineEmits<{
    (on:"addressUpdate", value: Address): void
}>();

async function getSuggestions(address: string) {
    if (address.length >= 2) {
        try {
            suggestions.value = await fetchAddressSuggestions(
                { getAddressBaseURL: configStore.getAddressBaseURL, getAddressKey: configStore.getAddressKey },
                address);
        } catch (err) {
            captureException(err);
            toast({
                timeout: configStore.toastTimeout,
                title: "Could not retrieve addresses",
                message: "Please try again in a few minutes or enter the address manually.",
            });
        }
    }
}

async function suggestionClick(suggestion: Suggestion) {
    try {
        const address = await fetchAddress(
            { getAddressBaseURL: configStore.getAddressBaseURL, getAddressKey: configStore.getAddressKey },
            suggestion.id.toString());
        addressState.line1 = address.line1 ?? "";
        addressState.line2 = address.line2 ?? "";
        addressState.city = address.city ?? "";
        addressState.county = address.county ?? "";
        addressState.postcode = address.postcode ?? "";
        addressState.country = "United Kingdom";
        // need manually trigger validation here as prepop doesn't trigger on blur event
        v$.value.$touch();
    } catch (err) {
        captureException(err);
        toast({
            timeout: configStore.toastTimeout,
            title: "Could not pre-populate address",
            message: "Please try again in a few minutes or enter the address manually.",
        });
    }
}

</script>
<template>
    <Question
        :name="idWithPrepend('addressSearch')"
        :label="findAddressLabel"
        :input-class="questionRow ? 'col-md-8' : undefined"
        :label-class="questionRow ? 'order-first col-md-2 col-form-label pt-0' : undefined"
    >
        <AutoComplete
            :id="idWithPrepend('addressSearch')"
            :suggestions="suggestions"
            not-found-message="Cannot find address"
            placeholder="Start typing..."
            :clear-input-after-click="true"
            @update:suggestion="getSuggestions"
            @click:suggestion="suggestionClick"
        />
    </Question>
    <Question
        :name="idWithPrepend('line1')"
        label="Address line 1"
        :input-class="questionRow ? 'col-md-8' : undefined"
        :label-class="questionRow ? 'order-first col-md-2 col-form-label pt-0' : undefined"
    >
        <Input
            :id="idWithPrepend('line1')"
            v-model="addressState.line1"
            placeholder="Line 1"
            type="text"
            :validation="addressValidator.line1"
        />
    </Question>
    <Question
        :name="idWithPrepend('line2')"
        label="Address line 2"
        sub-label="Optional"
        :input-class="questionRow ? 'col-md-8' : undefined"
        :label-class="questionRow ? 'order-first col-md-2 col-form-label pt-0' : undefined"
    >
        <Input
            :id="idWithPrepend('line2')"
            v-model="addressState.line2"
            placeholder="Line 2"
            type="text"
        />
    </Question>
    <Question
        :name="idWithPrepend('city')"
        label="City"
        :input-class="questionRow ? 'col-md-8' : undefined"
        :label-class="questionRow ? 'order-first col-md-2 col-form-label pt-0' : undefined"
    >
        <Input
            :id="idWithPrepend('city')"
            v-model="addressState.city"
            placeholder="City"
            type="text"
            :validation="addressValidator.city"
        />
    </Question>
    <Question
        :name="idWithPrepend('county')"
        label="County"
        :input-class="questionRow ? 'col-md-8' : undefined"
        :label-class="questionRow ? 'order-first col-md-2 col-form-label pt-0' : undefined"
    >
        <Input
            :id="idWithPrepend('county')"
            v-model="addressState.county"
            placeholder="County"
            type="text"
            :validation="addressValidator.county"
        />
    </Question>
    <Question
        :name="idWithPrepend('postcode')"
        label="Postcode"
        :input-class="questionRow ? 'col-md-8' : undefined"
        :label-class="questionRow ? 'order-first col-md-2 col-form-label pt-0' : undefined"
    >
        <Input
            :id="idWithPrepend('postcode')"
            v-model="addressState.postcode"
            input-class="input-width-auto"
            placeholder="Postcode"
            type="text"
            :validation="{...addressValidator.postcode, ...samePostcodeValidator(props.postcodeVerify) }"
        />
    </Question>
    <Question
        :name="idWithPrepend('country')"
        label="Country"
        :input-class="questionRow ? 'col-md-8' : undefined"
        :label-class="questionRow ? 'order-first col-md-2 col-form-label pt-0' : undefined"
    >
        <Input
            :id="idWithPrepend('country')"
            v-model="addressState.country"
            placeholder="Country"
            type="text"
            :validation="addressValidator.country"
        />
    </Question>
</template>

<style>
.static-input {
    font-weight: 500;
}
</style>
